import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", tool_name: "", price: 500, duration_days: 30, description: "", active: true };

export default function AdminPlans() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/plans").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      tool_name: p.tool_name,
      price: p.price,
      duration_days: p.duration_days,
      description: p.description || "",
      active: p.active,
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, duration_days: parseInt(form.duration_days) || 30 };
      if (editing) {
        await api.put(`/admin/plans/${editing.id}`, payload);
        toast.success("Plan updated");
      } else {
        await api.post("/admin/plans", payload);
        toast.success("Plan created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      await api.delete(`/admin/plans/${id}`);
      toast.success("Deleted");
      load();
    } catch (_) {
      toast.error("Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Plans</h1>
          <p className="text-sm text-muted-foreground mt-1">Sell shared AI tool access.</p>
        </div>
        <Button onClick={openNew} data-testid="add-plan">
          <Plus className="w-4 h-4 mr-2" /> Add Plan
        </Button>
      </div>

      <div className="glass-card rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Tool</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Active</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.tool_name}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">৳{p.price}</TableCell>
                  <TableCell className="font-mono text-xs">{p.duration_days}d</TableCell>
                  <TableCell>
                    <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm ${p.active ? "badge-paid" : "badge-unpaid"}`}>
                      {p.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-plan-${p.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="text-red-500" data-testid={`del-plan-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Plan" : "Add Plan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Plan Name</Label>
              <Input data-testid="plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Tool Name</Label>
              <Input data-testid="plan-tool" placeholder="e.g. ChatGPT Plus" value={form.tool_name} onChange={(e) => setForm({ ...form, tool_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Price (৳)</Label>
                <Input data-testid="plan-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Duration (days)</Label>
                <Input data-testid="plan-duration" type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Description</Label>
              <Textarea data-testid="plan-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="flex items-center justify-between py-1">
              <Label className="text-sm">Active (visible to users)</Label>
              <Switch data-testid="plan-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="plan-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="plan-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
